/**
 * PsicoSalud — Evolución longitudinal de una escala psicométrica.
 *
 * Serie única (el puntaje de una escala en el tiempo) dibujada en SVG,
 * sin librería de gráficos: mantiene el bundle liviano para el
 * despliegue en capa gratuita y evita una dependencia más que auditar.
 *
 * Decisiones de lectura:
 * - El eje Y arranca en 0 y llega al máximo de la escala. Recortarlo
 *   exageraría visualmente cambios que pueden ser ruido de medición.
 * - Una línea de referencia marca el punto de remisión del instrumento.
 * - Solo la primera y la última medición llevan etiqueta directa; el
 *   resto se consulta al pasar el puntero.
 * - Una sola serie no necesita leyenda: el título la nombra.
 * - La tabla de mediciones acompaña siempre al gráfico (la vista
 *   tabular es la alternativa accesible a la lectura visual).
 */

import { useId, useMemo, useState } from 'react';

import type { TrendMeasurement } from '@/types';

interface Props {
  measurements: TrendMeasurement[];
  maxScore: number;
  remissionCutoff?: number;
  scaleCode: string;
}

const WIDTH = 720;
const HEIGHT = 260;
const PADDING = { top: 20, right: 26, bottom: 34, left: 40 };

const SERIES_COLOR = '#2b7fb8';
const GRID_COLOR = '#d3dae1';
const AXIS_INK = '#5b6b7c';
const SURFACE = '#ffffff';

export default function ScoreTrendChart({
  measurements,
  maxScore,
  remissionCutoff,
  scaleCode,
}: Props) {
  const clipId = useId();
  const [hovered, setHovered] = useState<number | null>(null);

  const plotWidth = WIDTH - PADDING.left - PADDING.right;
  const plotHeight = HEIGHT - PADDING.top - PADDING.bottom;

  const points = useMemo(() => {
    const times = measurements.map((m) => new Date(m.fecha).getTime());
    const min = Math.min(...times);
    const max = Math.max(...times);
    // Con todas las mediciones en la misma fecha, el rango temporal es
    // cero: se reparten de forma equidistante para que sigan siendo
    // legibles en lugar de apilarse en un punto.
    const span = max - min || 1;

    return measurements.map((measurement, index) => {
      const ratio =
        max === min
          ? measurements.length === 1
            ? 0.5
            : index / (measurements.length - 1)
          : (new Date(measurement.fecha).getTime() - min) / span;

      return {
        ...measurement,
        x: PADDING.left + ratio * plotWidth,
        y: PADDING.top + plotHeight - (measurement.puntaje / maxScore) * plotHeight,
      };
    });
  }, [measurements, maxScore, plotWidth, plotHeight]);

  if (points.length === 0) return null;

  const path = points.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((fraction) => ({
    value: Math.round(maxScore * fraction),
    y: PADDING.top + plotHeight - fraction * plotHeight,
  }));

  const cutoffY =
    remissionCutoff !== undefined
      ? PADDING.top + plotHeight - (remissionCutoff / maxScore) * plotHeight
      : null;

  const active = hovered !== null ? points[hovered] : null;

  return (
    <figure style={{ margin: 0 }} data-cy="trend-chart">
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        width="100%"
        role="img"
        aria-label={`Evolución del puntaje ${scaleCode} a lo largo del tratamiento`}
        style={{ display: 'block', overflow: 'visible' }}
      >
        <defs>
          <clipPath id={clipId}>
            <rect x={PADDING.left} y={PADDING.top} width={plotWidth} height={plotHeight} />
          </clipPath>
        </defs>

        {/* Grilla y escala vertical, deliberadamente discretas */}
        {ticks.map((tick) => (
          <g key={tick.value}>
            <line
              x1={PADDING.left}
              x2={WIDTH - PADDING.right}
              y1={tick.y}
              y2={tick.y}
              stroke={GRID_COLOR}
              strokeWidth={1}
            />
            <text
              x={PADDING.left - 8}
              y={tick.y + 4}
              textAnchor="end"
              fontSize={11}
              fill={AXIS_INK}
            >
              {tick.value}
            </text>
          </g>
        ))}

        {/* Referencia clínica: umbral de remisión del instrumento */}
        {cutoffY !== null && (
          <g>
            <line
              x1={PADDING.left}
              x2={WIDTH - PADDING.right}
              y1={cutoffY}
              y2={cutoffY}
              stroke={AXIS_INK}
              strokeWidth={1}
              strokeDasharray="5 4"
            />
            <text x={WIDTH - PADDING.right} y={cutoffY - 6} textAnchor="end" fontSize={11} fill={AXIS_INK}>
              Remisión ({remissionCutoff})
            </text>
          </g>
        )}

        <polyline
          points={path}
          fill="none"
          stroke={SERIES_COLOR}
          strokeWidth={2}
          strokeLinejoin="round"
          strokeLinecap="round"
          clipPath={`url(#${clipId})`}
        />

        {points.map((point, index) => {
          const isEdge = index === 0 || index === points.length - 1;
          return (
            <g key={`${point.fecha}-${index}`}>
              {/* Anillo del color de la superficie: separa el marcador
                  de la línea cuando los puntos se aproximan. */}
              <circle cx={point.x} cy={point.y} r={5.5} fill={SURFACE} />
              <circle cx={point.x} cy={point.y} r={4.5} fill={SERIES_COLOR} />

              {(isEdge || hovered === index) && (
                <text
                  x={point.x}
                  y={point.y - 12}
                  textAnchor="middle"
                  fontSize={11}
                  fontWeight={600}
                  fill={AXIS_INK}
                >
                  {point.puntaje}
                </text>
              )}

              {/* Zona de contacto mayor que el marcador visible */}
              <circle
                cx={point.x}
                cy={point.y}
                r={16}
                fill="transparent"
                onMouseEnter={() => setHovered(index)}
                onMouseLeave={() => setHovered(null)}
                style={{ cursor: 'pointer' }}
              />
            </g>
          );
        })}

        {/* Eje temporal: solo los extremos, para no saturar */}
        <text x={PADDING.left} y={HEIGHT - 10} fontSize={11} fill={AXIS_INK}>
          {new Date(points[0].fecha).toLocaleDateString('es-PY')}
        </text>
        {points.length > 1 && (
          <text x={WIDTH - PADDING.right} y={HEIGHT - 10} textAnchor="end" fontSize={11} fill={AXIS_INK}>
            {new Date(points[points.length - 1].fecha).toLocaleDateString('es-PY')}
          </text>
        )}
      </svg>

      {active && (
        <figcaption className="t-pequeno t-suave" style={{ marginTop: '0.3rem' }} data-cy="trend-tooltip">
          {new Date(active.fecha).toLocaleDateString('es-PY')} · {scaleCode}: {active.puntaje}/{maxScore}
        </figcaption>
      )}
    </figure>
  );
}
